import { z } from 'zod'

import type { SupportedMarketKey } from '@nxt-insurance/constants'
import { NO_INVALID_CHARACTER_USERNAME_REGEX } from '@nxt-insurance/constants'

import { getPhoneNumberSchema } from '@flows/configs/common/steps/email_and_name'
import { t } from '@flows/core/i18n'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

const getNameSchema = () =>
  z
    .string()
    .min(2)
    .max(50)
    .regex(
      NO_INVALID_CHARACTER_USERNAME_REGEX,
      t('sharedFlows_common:generic.errors.name_invalid_characters')
    )
export const getLoginSchema = () =>
  z.object({
    first_name: getNameSchema(),
    last_name: getNameSchema(),
    email: z.string().email(),
    phone_number: getPhoneNumberSchema(),
    accepted_erstinformationen: z.coerce
      .string()
      .pipe(z.enum(['true']))
      .transform(() => true),
  })

export const login = ({
  market,
  platform,
}: {
  market: SupportedMarketKey
  platform: 'web' | 'native'
}) => {
  return {
    schema: getLoginSchema(),
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.login.title',
        },
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.login.text',
        },
      },
      {
        type: 'columns',
        content: [
          {
            type: 'input',
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:steps.email_and_name.firstName.input.label',
            },
            shouldTrackValues: true,
            name: 'first_name',
          },
          {
            type: 'input',
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:steps.email_and_name.lastName.input.label',
            },
            shouldTrackValues: true,
            name: 'last_name',
          },
        ],
      },
      {
        type: 'input',
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.email_and_name.email.input.label',
        },
        shouldTrackValues: true,
        name: 'email',
      },
      {
        type: 'phoneNumber',
        name: 'phone_number',
        disabledIfValueExists: true,
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.email_and_name.phone.input.label',
        },
        defaultCountry: market,
        isOptional: false,
      },
      {
        type: 'checkbox',
        fields: [
          {
            name: 'accepted_erstinformationen',
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:steps.login.checkbox.label',
            },
          },
        ],
      },
    ],
    onSubmit: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'updateAndCompleteConsultationRequest',
        inputs: [],
        outputs: [],
      },
    },
    nextStep: 'success',
    skipStep: {
      condition: () => platform === 'native',
      targetStepName: 'phone_confirmation',
    },
    subFlow: SubFlow.Common,
  } as const satisfies AnyStepConfiguration
}
